import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Brain, TrendingUp, Star, Target, Users, Award,
    ChevronDown, ChevronUp, Search, Filter, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPORTS = [
    "Athletics Track and Field", "Basketball", "Hockey", "Football", "Volleyball",
    "Rugby", "Netball", "Chess", "Table Tennis", "Karate", "Taekwondo",
    "Badminton", "Swimming", "Tennis", "Handball", "Judo", "Padel",
    "Squash", "E-Sports", "Motor Racing"
];

const AGE_CATEGORIES = ["U10", "U12", "U15", "U18", "U23", "Open"];

const projectionColors = {
    "Olympic Candidate": "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
    "National Team Prospect": "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    "Regional Talent": "text-blue-400 bg-blue-400/10 border-blue-400/30",
    "Developing Talent": "text-orange-400 bg-orange-400/10 border-orange-400/30",
    "Early Stage": "text-gray-400 bg-gray-400/10 border-gray-400/30",
};

const ScoreBar = ({ value, max = 100, color = "bg-primary" }) => (
    <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
        <div 
            className={`h-full rounded-full transition-all duration-500 ${color}`}
            style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
        />
    </div>
);

const TalentAITab = ({ token }) => {
    const [view, setView] = useState('dashboard');
    const [trends, setTrends] = useState(null);
    const [scores, setScores] = useState(null);
    const [prospects, setProspects] = useState(null);
    const [selectedAthlete, setSelectedAthlete] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sportFilter, setSportFilter] = useState('');
    const [ageFilter, setAgeFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: { Authorization: `Bearer ${token}` }
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (sportFilter) params.sport = sportFilter;
            if (ageFilter) params.age_category = ageFilter;

            const [trendsRes, scoresRes, prospectsRes] = await Promise.all([
                api.get('/ai/talent-intelligence/trends', { params }),
                api.get('/ai/talent-intelligence/scores', { params: { ...params, limit: 100 } }),
                api.get('/ai/talent-intelligence/top-prospects', { params: { ...params, limit: 20 } }),
            ]);
            setTrends(trendsRes.data);
            setScores(scoresRes.data);
            setProspects(prospectsRes.data);
        } catch (err) {
            console.error('Failed to load talent data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [sportFilter, ageFilter]);

    const loadAthleteProfile = async (athleteId) => {
        try {
            const res = await api.get(`/ai/talent-intelligence/athlete/${athleteId}`);
            setSelectedAthlete(res.data);
            setView('profile');
        } catch (err) {
            console.error('Failed to load athlete profile:', err);
        }
    };

    const filteredScores = scores?.athletes?.filter(a =>
        !searchQuery || a.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.national_athlete_id?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    if (loading && !trends) {
        return (
            <div className="flex items-center justify-center py-20" data-testid="talent-ai-loading">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Computing talent intelligence...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="talent-ai-tab">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Brain className="w-5 h-5 text-primary" />
                        TALENT INTELLIGENCE AI
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                        AI-powered athlete scoring, projections, and development tracking
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button 
                        variant={view === 'dashboard' ? 'default' : 'outline'} 
                        size="sm" 
                        onClick={() => setView('dashboard')}
                        data-testid="talent-view-dashboard"
                    >
                        Dashboard
                    </Button>
                    <Button 
                        variant={view === 'scores' ? 'default' : 'outline'} 
                        size="sm" 
                        onClick={() => setView('scores')}
                        data-testid="talent-view-scores"
                    >
                        All Scores
                    </Button>
                    <Button 
                        variant={view === 'prospects' ? 'default' : 'outline'} 
                        size="sm" 
                        onClick={() => setView('prospects')}
                        data-testid="talent-view-prospects"
                    >
                        Top Prospects
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
                <select 
                    className="bg-[#0d2847] border border-white/10 text-sm px-3 py-2 rounded text-white"
                    value={sportFilter} 
                    onChange={e => setSportFilter(e.target.value)}
                    data-testid="talent-sport-filter"
                >
                    <option value="">All Sports</option>
                    {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select 
                    className="bg-[#0d2847] border border-white/10 text-sm px-3 py-2 rounded text-white"
                    value={ageFilter} 
                    onChange={e => setAgeFilter(e.target.value)}
                    data-testid="talent-age-filter"
                >
                    <option value="">All Ages</option>
                    {AGE_CATEGORIES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                {(view === 'scores' || view === 'prospects') && (
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                        <Input 
                            placeholder="Search athlete or ID..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="pl-9 bg-[#0d2847] border-white/10 h-9 text-sm"
                            data-testid="talent-search"
                        />
                    </div>
                )}
            </div>

            {/* Dashboard View */}
            {view === 'dashboard' && trends && <DashboardView trends={trends} prospects={prospects} onSelectAthlete={loadAthleteProfile} />}

            {/* Scores View */}
            {view === 'scores' && <ScoresView athletes={filteredScores} total={scores?.total} onSelectAthlete={loadAthleteProfile} />}

            {/* Prospects View */}
            {view === 'prospects' && <ProspectsView prospects={prospects} searchQuery={searchQuery} onSelectAthlete={loadAthleteProfile} />}

            {/* Athlete Profile View */}
            {view === 'profile' && selectedAthlete && (
                <AthleteProfileView 
                    athlete={selectedAthlete} 
                    onBack={() => setView('dashboard')}
                />
            )}
        </div>
    );
};

const DashboardView = ({ trends, prospects, onSelectAthlete }) => (
    <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(trends.by_projection).map(([label, count]) => (
                <div key={label} className={`border rounded-lg p-4 ${projectionColors[label] || 'border-white/10'}`} data-testid={`projection-${label.replace(/\s+/g, '-').toLowerCase()}`}>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs mt-1 opacity-80">{label}</p>
                </div>
            ))}
        </div>

        {/* Avg Score + Total */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#0d2847] border border-white/5 rounded-lg p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> TALENT BY SPORT
                </h3>
                <div className="space-y-3">
                    {trends.by_sport.slice(0, 8).map(s => (
                        <div key={s.sport} className="flex items-center gap-3">
                            <span className="text-xs w-32 truncate">{s.sport}</span>
                            <div className="flex-1">
                                <ScoreBar value={s.avg_score} color={s.avg_score >= 70 ? 'bg-emerald-500' : s.avg_score >= 50 ? 'bg-blue-500' : 'bg-orange-500'} />
                            </div>
                            <span className="text-xs font-mono w-10 text-right">{s.avg_score}</span>
                            <span className="text-xs text-muted-foreground w-6 text-right">{s.count}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-[#0d2847] border border-white/5 rounded-lg p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4" /> AGE CATEGORY BREAKDOWN
                </h3>
                <div className="space-y-3">
                    {Object.entries(trends.by_age_category).sort((a, b) => b[1].avg_score - a[1].avg_score).map(([age, data]) => (
                        <div key={age} className="flex items-center gap-3">
                            <span className="text-xs w-12 font-mono">{age}</span>
                            <div className="flex-1">
                                <ScoreBar value={data.avg_score} color="bg-primary" />
                            </div>
                            <span className="text-xs font-mono w-10 text-right">{data.avg_score}</span>
                            <span className="text-xs text-muted-foreground w-6 text-right">{data.count}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Top Prospects */}
        {prospects && prospects.prospects?.length > 0 && (
            <div className="bg-[#0d2847] border border-white/5 rounded-lg p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-400" /> TOP EMERGING PROSPECTS
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {prospects.prospects.slice(0, 8).map((p, i) => (
                        <div 
                            key={p.athlete_id} 
                            className="border border-white/10 rounded-lg p-4 cursor-pointer hover:border-primary/50 transition-colors"
                            onClick={() => onSelectAthlete(p.athlete_id)}
                            data-testid={`prospect-card-${i}`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-mono text-primary">#{i + 1}</span>
                                <span className={`text-xs px-2 py-0.5 rounded border ${projectionColors[p.future_projection] || ''}`}>
                                    {p.future_projection}
                                </span>
                            </div>
                            <p className="font-medium text-sm truncate">{p.full_name}</p>
                            <p className="text-xs text-muted-foreground">{p.sport} | {p.age_category}</p>
                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                <div>
                                    <span className="text-muted-foreground">Talent</span>
                                    <p className="font-mono font-bold">{p.talent_score}</p>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">Elite %</span>
                                    <p className="font-mono font-bold">{p.elite_potential}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
);

const ScoresView = ({ athletes, total, onSelectAthlete }) => (
    <div className="bg-[#0d2847] border border-white/5 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{total} athletes scored</span>
        </div>
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="talent-scores-table">
                <thead className="bg-white/5">
                    <tr>
                        <th className="text-left p-3 text-xs text-muted-foreground">#</th>
                        <th className="text-left p-3 text-xs text-muted-foreground">Athlete</th>
                        <th className="text-left p-3 text-xs text-muted-foreground">Sport</th>
                        <th className="text-left p-3 text-xs text-muted-foreground">Age</th>
                        <th className="text-center p-3 text-xs text-muted-foreground">Talent Score</th>
                        <th className="text-center p-3 text-xs text-muted-foreground">Growth</th>
                        <th className="text-center p-3 text-xs text-muted-foreground">Consistency</th>
                        <th className="text-center p-3 text-xs text-muted-foreground">Elite %</th>
                        <th className="text-left p-3 text-xs text-muted-foreground">Projection</th>
                    </tr>
                </thead>
                <tbody>
                    {athletes.map((a, i) => (
                        <tr 
                            key={a.athlete_id} 
                            className="border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                            onClick={() => onSelectAthlete(a.athlete_id)}
                            data-testid={`score-row-${i}`}
                        >
                            <td className="p-3 text-muted-foreground">{i + 1}</td>
                            <td className="p-3">
                                <p className="font-medium">{a.full_name}</p>
                                <p className="text-xs text-muted-foreground">{a.national_athlete_id || 'No ID'}</p>
                            </td>
                            <td className="p-3 text-xs">{a.sport}</td>
                            <td className="p-3 text-xs font-mono">{a.age_category}</td>
                            <td className="p-3 text-center">
                                <span className={`font-mono font-bold ${a.talent_score >= 80 ? 'text-emerald-400' : a.talent_score >= 60 ? 'text-blue-400' : 'text-orange-400'}`}>
                                    {a.talent_score}
                                </span>
                            </td>
                            <td className="p-3 text-center font-mono text-xs">{a.growth_rate}</td>
                            <td className="p-3 text-center font-mono text-xs">{a.consistency_index}</td>
                            <td className="p-3 text-center font-mono text-xs">{a.elite_potential}</td>
                            <td className="p-3">
                                <span className={`text-xs px-2 py-0.5 rounded border ${projectionColors[a.future_projection] || ''}`}>
                                    {a.future_projection}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const ProspectsView = ({ prospects, searchQuery, onSelectAthlete }) => {
    const filtered = prospects?.prospects?.filter(p =>
        !searchQuery || p.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.national_athlete_id?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{filtered.length} prospects with elite potential 40+</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((p, i) => (
                    <div 
                        key={p.athlete_id} 
                        className="bg-[#0d2847] border border-white/10 rounded-lg p-5 cursor-pointer hover:border-primary/50 transition-all"
                        onClick={() => onSelectAthlete(p.athlete_id)}
                        data-testid={`prospect-detail-${i}`}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <p className="font-semibold">{p.full_name}</p>
                                <p className="text-xs text-muted-foreground">{p.sport} | {p.age_category} | {p.country}</p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded border ${projectionColors[p.future_projection] || ''}`}>
                                {p.future_projection}
                            </span>
                        </div>
                        <div className="grid grid-cols-4 gap-3 text-center">
                            <div>
                                <p className="text-xs text-muted-foreground">Talent</p>
                                <p className="font-mono font-bold text-lg">{p.talent_score}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Growth</p>
                                <p className="font-mono font-bold text-lg">{p.growth_rate}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Consistency</p>
                                <p className="font-mono font-bold text-lg">{p.consistency_index}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Elite %</p>
                                <p className="font-mono font-bold text-lg text-primary">{p.elite_potential}</p>
                            </div>
                        </div>
                        {p.school && <p className="text-xs text-muted-foreground mt-3">School: {p.school}</p>}
                        {p.club && <p className="text-xs text-muted-foreground">Club: {p.club}</p>}
                        {p.national_athlete_id && <p className="text-xs font-mono text-primary/70 mt-1">{p.national_athlete_id}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
};

const AthleteProfileView = ({ athlete, onBack }) => (
    <div className="space-y-6">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="talent-profile-back">
            Back to Dashboard
        </Button>

        <div className="bg-[#0d2847] border border-white/5 rounded-lg p-6">
            <div className="flex flex-col md:flex-row justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold">{athlete.full_name}</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        {athlete.sport} | {athlete.age_category} | {athlete.country}
                    </p>
                    {athlete.national_athlete_id && (
                        <p className="text-xs font-mono text-primary mt-1">{athlete.national_athlete_id}</p>
                    )}
                    {athlete.school && <p className="text-xs text-muted-foreground mt-1">School: {athlete.school}</p>}
                    {athlete.club && <p className="text-xs text-muted-foreground">Club: {athlete.club}</p>}
                </div>
                <div className="flex items-center gap-2">
                    <span className={`px-3 py-1.5 rounded border text-sm font-medium ${projectionColors[athlete.future_projection] || ''}`}>
                        {athlete.future_projection}
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-white/10">ELO: {athlete.elo_rating}</span>
                    <span className="text-xs px-2 py-1 rounded bg-white/10">{athlete.tier}</span>
                </div>
            </div>
        </div>

        {/* Talent Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
                { label: "Talent Score", value: athlete.talent_score, color: "text-emerald-400" },
                { label: "Growth Rate", value: athlete.growth_rate, color: "text-blue-400" },
                { label: "Consistency", value: athlete.consistency_index, color: "text-purple-400" },
                { label: "Elite Potential", value: athlete.elite_potential, color: "text-yellow-400" },
                { label: "ELO Rating", value: athlete.elo_rating, color: "text-primary" },
            ].map(m => (
                <div key={m.label} className="bg-[#0d2847] border border-white/5 rounded-lg p-4 text-center" data-testid={`metric-${m.label.replace(/\s+/g, '-').toLowerCase()}`}>
                    <p className="text-xs text-muted-foreground">{m.label}</p>
                    <p className={`text-3xl font-bold font-mono mt-1 ${m.color}`}>{m.value}</p>
                </div>
            ))}
        </div>

        {/* Match History */}
        {athlete.match_history?.length > 0 && (
            <div className="bg-[#0d2847] border border-white/5 rounded-lg p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-4">MATCH HISTORY</h3>
                <div className="space-y-2">
                    {athlete.match_history.map((m, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm py-2 border-b border-white/5 last:border-0">
                            <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                                m.result === 'W' ? 'bg-emerald-500/20 text-emerald-400' : 
                                m.result === 'L' ? 'bg-red-500/20 text-red-400' : 
                                'bg-gray-500/20 text-gray-400'
                            }`}>
                                {m.result}
                            </span>
                            <span className="flex-1">vs {m.opponent}</span>
                            <span className="font-mono text-xs">{m.score}</span>
                            <span className="text-xs text-muted-foreground truncate max-w-[200px]">{m.tournament}</span>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
);

export default TalentAITab;
